import * as React from "react";
import styled from "styled-components";
import { useCalendarContext } from "../CalendarContext";
import { Colors } from "@blueprintjs/core";
import { DayInfos } from "../utils/dates";

const Container = styled.div<{ height: number }>`
  display: flex;
  height: ${(p) => p.height}px;
  flex-direction: column;
  border-top: 1px solid ${Colors.BLACK};
  flex: 1;
  overflow: hidden;
`;

const SplitDayContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  flex: 1;

  & > * + * {
    border-left: 1px dotted black;
  }
`;

const SplitDayInnerBlock = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: stretch;
  flex: 1;
  width: 50%;
`;

const FullDayInnerBlock = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: stretch;
  flex: 1;
  overflow: hidden;
`;

const EventContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  > * {
    text-overflow: ellipsis;
    overflow: hidden;
  }
  overflow: hidden;
`;

const FullDayContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
`;

export interface ResourceDayBlockProps {
  resourceKey: any;
  day: DayInfos;
}

export function ResourceDayBlock({ resourceKey, day }: ResourceDayBlockProps) {
  const { horizontalResources, getDayEvents, renderEvent, isWholeDay } =
    useCalendarContext();

  const events = React.useMemo(() => {
    const dayEvents = getDayEvents(day);
    const filtered = dayEvents.filter((d) =>
      horizontalResources.checkEventByKey(resourceKey, d)
    );
    return filtered;
  }, [day, getDayEvents, horizontalResources, resourceKey]);

  const morningEvents = React.useMemo(
    () =>
      events.filter(
        (e) => e.startDate.getHours() <= 12 && e.endDate.getHours() <= 12
      ),
    [events]
  );
  const afternoonEvents = React.useMemo(
    () =>
      events.filter(
        (e) => e.startDate.getHours() >= 12 && e.endDate.getHours() >= 12
      ),
    [events]
  );

  const wholeDayEvents = React.useMemo(
    () =>
      events.filter((e) =>
        isWholeDay(e.startDate.getHours(), e.endDate.getHours())
      ),
    [events, isWholeDay]
  );
  const morningEventWidth = React.useMemo(
    () => 100 / morningEvents.length,
    [morningEvents.length]
  );
  const afternoonEventWidth = React.useMemo(
    () => 100 / afternoonEvents.length,
    [afternoonEvents.length]
  );
  return (
    <Container height={horizontalResources.resourceRowHeightInPx}>
      <FullDayContainer>
        {wholeDayEvents.map((e, i) => (
          <FullDayInnerBlock>
            <EventContainer key={i}>{renderEvent(e.obj)}</EventContainer>
          </FullDayInnerBlock>
        ))}
      </FullDayContainer>
      <SplitDayContainer>
        <SplitDayInnerBlock>
          {morningEvents.map((e, i) => (
            <EventContainer key={i} style={{ width: morningEventWidth + "%" }}>
              {renderEvent(e.obj)}
            </EventContainer>
          ))}
        </SplitDayInnerBlock>
        <SplitDayInnerBlock>
          {afternoonEvents.map((e, i) => (
            <EventContainer
              key={i}
              style={{ width: afternoonEventWidth + "%" }}
            >
              {renderEvent(e.obj)}
            </EventContainer>
          ))}
        </SplitDayInnerBlock>
      </SplitDayContainer>
    </Container>
  );
}
